import EventEmitter from 'events'
import { FC, forwardRef, useEffect } from 'react'

export type TSite = 'admin' | 'client' | 'music' | 'home' | (string & {})

interface IEventOptions {
  message?: string
  href?: string
  target?: string
  rel?: string
  site?: TSite
}

export interface IMfeNavigationEventConfigs {
  navigation: [{ routeKey?: string; options?: IEventOptions }]
}

export class MfeNavigationEventStore extends EventEmitter<IMfeNavigationEventConfigs> {
  constructor() {
    super()
  }
  /**
   * Subscribe to a specific channel
   * @param channel The channel to subscribe to
   * @param callback Function to call when an event occurs on this channel
   * @returns Unsubscribe function to remove the event listener
   */
  _createWrapListener = (callback: (...payload: any[]) => void) => {
    return (...payload: any[]) => {
      try {
        callback(...payload)
      } catch (error) {
        console.error('Error in MfeBridge listener:', error)
      }
    }
  }
  subscribe<K extends keyof IMfeNavigationEventConfigs>(
    eventName: K,
    listener: K extends keyof IMfeNavigationEventConfigs
      ? IMfeNavigationEventConfigs[K] extends unknown[]
        ? (...args: IMfeNavigationEventConfigs[K]) => void
        : never
      : never,
    isTry: boolean = true
  ) {
    const action = isTry ? this._createWrapListener(listener) : listener
    super.addListener(eventName, action as any)
    return () => {
      super.removeListener(eventName, action as any)
    }
  }
}

export function getMfeNavigationEventStore() {
  if (typeof globalThis === undefined) return null

  if (typeof (globalThis as any).MfeNavigationEventStore === 'undefined') {
    // If MfeNavigationEventStore is not defined globally, define it
    // This allows it to be used in other parts of the application
    // without needing to import it explicitly
    ;(globalThis as any).MfeNavigationEventStore = new MfeNavigationEventStore()
  }
  const eventEmitter = (globalThis as any).MfeNavigationEventStore as MfeNavigationEventStore
  eventEmitter.setMaxListeners(50)
  return eventEmitter
}

type INavigationFunction = (routeKey?: string, options?: IEventOptions) => void

export interface IMfeNavigationProviderProps {
  navigation?: INavigationFunction
}

export const MfeNavigationProvider: FC<IMfeNavigationProviderProps> = (props) => {
  useEffect(() => {
    const eventStore = getMfeNavigationEventStore()
    if (!eventStore) return

    const unsubscribe = eventStore.subscribe('navigation', (payload) => {
      if (props.navigation) {
        props.navigation(payload.routeKey, payload.options)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  return <></>
}

export interface IMfeLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  routeKey?: string
  site?: TSite
}

export const MfeLink = forwardRef<HTMLAnchorElement, IMfeLinkProps>(({ onClick, routeKey, site, ...props }, ref) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(event)
    }
    if (event.defaultPrevented) {
      return // nothing to do if event already handled
    }
    const eventStore = getMfeNavigationEventStore()
    if (eventStore && !!routeKey) {
      event.preventDefault() // Prevent default link behavior
      event.stopPropagation() // Stop the event from bubbling up
      const { target, rel, href } = props
      const options: IEventOptions = { message: 'Link clicked' }
      if (target) options.target = target
      if (rel) options.rel = rel
      if (href) options.href = href
      if (site) options.site = site
      eventStore.emit('navigation', { routeKey: routeKey, options })
    }
  }
  return <a {...props} ref={ref} onClick={handleClick} />
})

export const mfeNavigation = (routeKey: string, options?: IEventOptions) => {
  const eventStore = getMfeNavigationEventStore()
  if (eventStore) {
    eventStore.emit('navigation', { routeKey: routeKey, options: { message: `Navigating to ${routeKey}`, ...options } })
  }
}
